'use client'

import { useMemo, useState } from 'react'
import Link from 'next/link'
import { useSearchParams } from 'next/navigation'
import { useLocale, useTranslations } from 'next-intl'
import { useRouter } from '@bprogress/next/app'
import { useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import {
  CalendarIcon,
  EditIcon,
  EyeIcon,
  MoreHorizontalIcon,
  SquareMenuIcon,
  SquarePlusIcon,
  TagIcon,
  TextIcon,
  TramFrontIcon,
  Trash2Icon,
  RefreshCwIcon
} from 'lucide-react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Checkbox } from '@/components/ui/checkbox'
import { Button } from '@/components/ui/button'
import { SortEnum } from '@/enums'
import { useDeleteModel, useModels, useBulkDeleteModels } from '@/hooks'
import { useModelsStore, useMeStore } from '@/store'
import { hasPermission } from '@/lib/permissions'
import { ModelsFilterParams } from '@/types/model'
import { DeleteConfirmationDialog, LoadingState, ModelsFilters, SortableHeader } from '@/components/partials'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { ContextMenu, ContextMenuContent, ContextMenuItem, ContextMenuTrigger } from '@/components/ui/context-menu'
import { formatDateTime } from '@/lib/utils'
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu'
import { DataTablePagination } from '@/components/ui/data-table-pagination'
import { BulkDeleteResponse } from '@/types/general'

const List = () => {
  const locale = useLocale()
  const t = useTranslations()
  const router = useRouter()
  const searchParams = useSearchParams()
  const queryClient = useQueryClient()
  const [deleteModelId, setDeleteModelId] = useState<string | null>(null)
  const [bulkDeleteConfirmOpen, setBulkDeleteConfirmOpen] = useState<boolean>(false)

  const { mutate: deleteModelMutate, isPending: isDeleting } = useDeleteModel()
  const { mutate: bulkDeleteModelsMutate, isPending: isBulkDeleting } = useBulkDeleteModels()

  const { meState } = useMeStore()
  const currentUser = meState.response
  const canCreate = hasPermission(currentUser, 'MODELS', 'CREATE')
  const canShow = hasPermission(currentUser, 'MODELS', 'SHOW')
  const canUpdate = hasPermission(currentUser, 'MODELS', 'UPDATE')
  const canDelete = hasPermission(currentUser, 'MODELS', 'DELETE')

  const selectedModelIds = useModelsStore(state => state.selectedModelIds)
  const toggleModelSelection = useModelsStore(state => state.toggleModelSelection)
  const selectAllModels = useModelsStore(state => state.selectAllModels)
  const deselectAllModels = useModelsStore(state => state.deselectAllModels)

  const filters = useMemo<ModelsFilterParams>(() => {
    const params = new URLSearchParams(searchParams.toString())

    const brandsParam = params.get('brandIds')
    const brands = brandsParam ? brandsParam.split(',').filter(Boolean) : undefined

    return {
      page: parseInt(params.get('page') || '1'),
      size: parseInt(params.get('size') || '20'),
      sort: (params.get('sort') as SortEnum) || SortEnum.ASC,
      sortBy: params.get('sortBy') || 'name',
      q: params.get('q') || '',
      brandIds: brands,
      createdAtStart: params.get('createdAtStart') || undefined,
      createdAtEnd: params.get('createdAtEnd') || undefined,
      updatedAtStart: params.get('updatedAtStart') || undefined,
      updatedAtEnd: params.get('updatedAtEnd') || undefined
    }
  }, [searchParams])
  const { data: modelsData, isLoading, error, refetch, isFetching } = useModels(filters)

  // Update URL when filters change
  const updateUrlParams = (newFilters: Partial<ModelsFilterParams>) => {
    const params = new URLSearchParams()
    const updatedFilters = { ...filters, ...newFilters }

    Object.entries(updatedFilters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        // Handle array values (like categories, brands)
        if (Array.isArray(value)) {
          if (value.length > 0) {
            params.set(key, value.join(','))
          }
        } else {
          params.set(key, value.toString())
        }
      }
    })

    router.push(`?${params.toString()}`)
  }

  const handleFiltersChange = (newFilters: Partial<ModelsFilterParams>) => {
    updateUrlParams(newFilters)
  }

  const handleResetFilters = () => {
    router.push('/products/models')
  }

  const handleSort = (sortBy: string, sort: SortEnum) => {
    handleFiltersChange({ sortBy, sort, page: 1 })
  }

  const handlePageChange = (page: number) => {
    handleFiltersChange({ page })
  }

  const handlePageSizeChange = (size: number) => {
    handleFiltersChange({ size, page: 1 })
  }

  const handleView = (modelId: string) => {
    router.push(`/products/models/${modelId}`)
  }

  const handleEdit = (modelId: string) => {
    router.push(`/products/models/${modelId}/edit`)
  }

  const handleDelete = (modelId: string) => {
    setDeleteModelId(modelId)
  }

  const renderActionItems = (modelId: string, isDropdown = true) => {
    const ItemComponent = isDropdown ? DropdownMenuItem : ContextMenuItem

    return (
      <>
        {canShow && (
          <ItemComponent
            onClick={e => {
              e.stopPropagation()
              handleView(modelId)
            }}>
            <EyeIcon className="h-4 w-4" />
            {t('model_details')}
          </ItemComponent>
        )}

        {canUpdate && (
          <ItemComponent
            onClick={e => {
              e.stopPropagation()
              handleEdit(modelId)
            }}>
            <EditIcon className="h-4 w-4" />
            {t('edit_model')}
          </ItemComponent>
        )}
        {canDelete && (
          <ItemComponent
            onClick={e => {
              e.stopPropagation()
              handleDelete(modelId)
            }}
            className="text-destructive focus:text-destructive">
            <Trash2Icon className="h-4 w-4" />
            {t('delete')}
          </ItemComponent>
        )}
      </>
    )
  }

  const confirmDeleteMessage = () => {
    if (deleteModelId) {
      deleteModelMutate(deleteModelId, {
        onSuccess: async () => {
          await queryClient.invalidateQueries({ queryKey: ['models'] })
          toast.success(t('deleted_successfully'))
          setDeleteModelId(null)
        },
        onError: (error: any) => {
          console.error('Error deleting model:', error)
          toast.error(error.response?.data.message ?? t('error_deleting'))
          setDeleteModelId(null)
        }
      })
    }
  }

  const handleBulkDelete = () => {
    if (selectedModelIds.length > 0) {
      setBulkDeleteConfirmOpen(true)
    }
  }

  const confirmBulkDeleteModels = () => {
    if (selectedModelIds.length > 0) {
      bulkDeleteModelsMutate(selectedModelIds, {
        onSuccess: async (response: BulkDeleteResponse) => {
          await queryClient.invalidateQueries({ queryKey: ['models'] })
          if (response.deletedItems && response.deletedItems.length > 0) {
            toast.success(t('deleted_successfully'))
          }
          setBulkDeleteConfirmOpen(false)
        },
        onError: (error: any) => {
          if (error.response) {
            if (error.response.status === 422) {
              toast.error(error.response.data?.items?.ids || t('error_deleting'))
            } else {
              toast.error(t('error_deleting'))
            }
          } else {
            toast.error(t('error_deleting'))
          }

          setBulkDeleteConfirmOpen(false)
        }
      })
    }
  }

  const handleSelectAll = () => {
    if (modelsData && modelsData.items.length > 0) {
      if (selectedModelIds.length === modelsData.items.length) {
        // If all models are selected, deselect all
        deselectAllModels()
      } else {
        // Otherwise, select all models
        selectAllModels(modelsData.items)
      }
    }
  }

  return (
    <>
      <Card>
        <CardHeader>
          <div className="grid gap-3 sm:flex sm:items-center sm:justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <TramFrontIcon className="h-5 w-5" />
                {t('models')}
              </CardTitle>
              <CardDescription>{t('models_description')}</CardDescription>
            </div>
            <div className="flex items-center justify-end gap-3">
              <Button variant="outline" size="icon" onClick={() => refetch()} disabled={isFetching || isLoading}>
                <RefreshCwIcon className={`h-4 w-4${isFetching ? 'animate-spin' : ''}`} />
              </Button>
              {canCreate && (
                <Button asChild>
                  <Link href="/products/models/create">
                    <SquarePlusIcon />
                    <span className="hidden lg:flex">{t('create_model')}</span>
                  </Link>
                </Button>
              )}
              {canDelete && selectedModelIds.length > 0 && (
                <Button variant="destructive" onClick={handleBulkDelete}>
                  <Trash2Icon className="h-4 w-4" />
                  <div className="hidden items-center gap-2 lg:flex">
                    <span>{t('delete_selected')}</span>
                    <span>({selectedModelIds.length})</span>
                  </div>
                </Button>
              )}
              <ModelsFilters
                filters={filters}
                onFiltersChange={handleFiltersChange}
                onResetFilters={handleResetFilters}
              />
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <LoadingState isLoading={isLoading} error={error?.message} onRetry={refetch} loadingRows={5}>
            {modelsData && (
              <div className="space-y-4">
                <div className="rounded-md border">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-12.5 text-center">
                          <div className="flex items-center justify-center">
                            {canDelete && (
                              <Checkbox
                                checked={
                                  modelsData &&
                                  modelsData.items.length > 0 &&
                                  selectedModelIds.length === modelsData.items.length
                                }
                                onCheckedChange={() => {
                                  handleSelectAll()
                                }}
                                onClick={e => {
                                  e.stopPropagation()
                                }}
                                className="cursor-pointer"
                              />
                            )}
                          </div>
                        </TableHead>
                        <TableHead className="min-w-50">
                          <SortableHeader
                            sortBy="name"
                            currentSortBy={filters.sortBy}
                            currentSort={filters.sort}
                            onSort={handleSort}>
                            <TextIcon className="h-4 w-4" />
                            {t('name')}
                          </SortableHeader>
                        </TableHead>
                        <TableHead className="min-w-50">
                          <SortableHeader
                            sortBy="brand"
                            currentSortBy={filters.sortBy}
                            currentSort={filters.sort}
                            onSort={handleSort}>
                            <TagIcon className="h-4 w-4" />
                            {t('brand')}
                          </SortableHeader>
                        </TableHead>
                        <TableHead className="min-w-50 text-center">
                          <SortableHeader
                            sortBy="createdAt"
                            currentSortBy={filters.sortBy}
                            currentSort={filters.sort}
                            onSort={handleSort}>
                            <CalendarIcon className="mr-2 w-4" />
                            {t('created_at')}
                          </SortableHeader>
                        </TableHead>
                        <TableHead className="min-w-50 text-center">
                          <SortableHeader
                            sortBy="updatedAt"
                            currentSortBy={filters.sortBy}
                            currentSort={filters.sort}
                            onSort={handleSort}>
                            <CalendarIcon className="mr-2 w-4" />
                            {t('updated_at')}
                          </SortableHeader>
                        </TableHead>
                        <TableHead className="w-25 text-center">
                          <div className="flex items-center justify-center gap-2">
                            <SquareMenuIcon className="h-4 w-4" />
                            {t('actions')}
                          </div>
                        </TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {modelsData.items.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={10} className="h-24 text-center">
                            {t('no_results')}
                          </TableCell>
                        </TableRow>
                      ) : (
                        modelsData.items.map((model, index) => (
                          <ContextMenu key={model.id}>
                            <ContextMenuTrigger asChild>
                              <TableRow
                                className={`hover:bg-muted/50 cursor-pointer transition-colors ${
                                  index % 2 === 0 ? 'bg-background' : 'bg-muted/25'
                                }`}
                                onClick={() => canShow && handleView(model.id)}>
                                <TableCell className="text-center">
                                  <div className="flex items-center justify-center">
                                    {canDelete && (
                                      <Checkbox
                                        checked={selectedModelIds.includes(model.id)}
                                        onCheckedChange={() => toggleModelSelection(model.id)}
                                        onClick={e => {
                                          e.stopPropagation()
                                        }}
                                        className="cursor-pointer"
                                      />
                                    )}
                                  </div>
                                </TableCell>
                                <TableCell>
                                  <div className="flex flex-col">
                                    <span>{model.name}</span>
                                    <span className="text-muted-foreground text-xs">ID: {model.id}</span>
                                  </div>
                                </TableCell>
                                <TableCell>{model.brand?.name || '-'}</TableCell>
                                <TableCell className="text-center">
                                  <div className="text-muted-foreground text-sm">
                                    <span>{formatDateTime(model.createdAt, locale)}</span>
                                  </div>
                                </TableCell>
                                <TableCell className="text-center">
                                  <div className="text-muted-foreground text-sm">
                                    <span>{formatDateTime(model.updatedAt, locale)}</span>
                                  </div>
                                </TableCell>
                                <TableCell className="text-center">
                                  <DropdownMenu>
                                    <DropdownMenuTrigger asChild>
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        className="h-8 w-8 p-0"
                                        onClick={e => {
                                          e.preventDefault()
                                          e.stopPropagation()
                                        }}>
                                        <span className="sr-only">{t('open_menu')}</span>
                                        <MoreHorizontalIcon className="h-4 w-4" />
                                      </Button>
                                    </DropdownMenuTrigger>
                                    <DropdownMenuContent align="end">
                                      {renderActionItems(model.id, true)}
                                    </DropdownMenuContent>
                                  </DropdownMenu>
                                </TableCell>
                              </TableRow>
                            </ContextMenuTrigger>
                            <ContextMenuContent>{renderActionItems(model.id, false)}</ContextMenuContent>
                          </ContextMenu>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </div>
                <DataTablePagination
                  currentPage={modelsData.page}
                  totalPages={modelsData.pages}
                  totalItems={modelsData.total}
                  pageSize={modelsData.size}
                  onPageChangeAction={handlePageChange}
                  onPageSizeChangeAction={handlePageSizeChange}
                />
              </div>
            )}
          </LoadingState>
        </CardContent>
      </Card>

      {canDelete && (
        <DeleteConfirmationDialog
          open={deleteModelId !== null}
          onCancel={() => setDeleteModelId(null)}
          onConfirm={confirmDeleteMessage}
          isDeleting={isDeleting}
        />
      )}

      {canDelete && (
        <DeleteConfirmationDialog
          open={bulkDeleteConfirmOpen}
          onCancel={() => setBulkDeleteConfirmOpen(false)}
          onConfirm={confirmBulkDeleteModels}
          isDeleting={isBulkDeleting}
        />
      )}
    </>
  )
}

export default List
